import { useEffect, useRef, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Link, useRouter } from '~/utils/navigation';
import { disableBodyScroll, enableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';
import { authenticationSelector } from '~/components/authentication/ducks/selectors.js';
import Icon, { ICON_HEART, ICON_HEART_FILLED, ICON_CLOSE } from '~/components/icon/index.js';
import { showNotification } from '~/components/notifications/ducks/notifications.js';
import { toggleLoveGame } from '../ducks/index';
import {
	gameDetailByGameCodeSelector,
	hasDepositLoginSelector,
	lovedGamesSelector,
	gameJackpotDataSelector,
	jackpotsSelector
} from '~/components/casino-shared/ducks/selectors';
import { selectCurrency, selectLocale } from '~/components/account/ducks/index.js';
import useTranslations from '~/components/translation/use-translations.ts';
import { selectHasRegulationsHeader } from '~/components/header/regulations-header/index.js';
import useLoginFlow from '~/hooks/use-login-flow.js';
import { selectIsRegularSiteType } from '~/components/brand/ducks/index.js';
import { errorHandler } from '~/components/casino-shared/explore/utilities/error-handler';
import useGameLauncher from '~/components/casino-shared/explore/utilities/use-game-launcher.js';
import { searchConversion } from '~/components/search/ducks/index';
import { isCasinoSearchPath } from '~/utils/pathname';
import useCasinoLocation from '~/hooks/use-casino-location.js';
import currencyFormatter from '~/libs/currency-formatter.js';
import {
	GameInfoContainer,
	ImageContainer,
	ExitButton,
	TitleContainer,
	PlayRealContainer,
	PlayRealButton,
	GameName,
	ProviderName,
	LoveButton,
	InfoButton,
	DetailsContainer,
	DetailsItem,
	DetailAmount,
	DetailTitle,
	VisibilityWrapper
	// DescriptionContainer
} from './game-info-old.styles.js';
import { GAME_MODE } from '~/components/casino-shared/constants';
import useCasinoRoutes from '~/components/router/use-casino-routes';
import useContextualPathTransformer from '~/hooks/use-contextual-path-transformer';
import useIsFunModeAllowed from '~/components/casino-shared/utilities/use-is-fun-mode-allowed';
import { getSmallestSrcFromSrcSet } from '~/components/utilities/image-format';
import useIngameImage from '~/components/casino-shared/utilities/use-ingame-image';
import { trackGameInfoPlayClick, trackGameInfoOpen, trackGameInfoClosed } from '@comeon/analytics';
import useConfiguration from '~/configuration/use-configuration';

const textKeys = [
	'PLAY',
	'PRACTICE_MODE',
	'JACKPOT',
	'PLAY_4_REAL',
	'MOBILE_LOGIN_LOGIN_BUTTON',
	'MOBILE_TRUSTLY_DEPOSIT_LOGIN',
	'LOVEGAMES_LOGOUT'
];

const GameInfoOld = () => {
	const router = useRouter();
	const location = useLocation();
	const forcedGameData = location?.state?.game;
	const gameCode = location?.state?.gameCode;
	const listName = location?.state?.listName;
	const listGameIndex = location?.state?.listGameIndex;
	const dispatch = useDispatch();
	const gameInfoRef = useRef(null);
	const transformPath = useContextualPathTransformer();
	const hasRegulationHeader = useSelector(selectHasRegulationsHeader);
	const authenticated = useSelector(authenticationSelector);
	const gameDetail = useSelector(state => gameDetailByGameCodeSelector(state, gameCode));
	const game = forcedGameData || gameDetail;
	const hasDepositLogin = useSelector(hasDepositLoginSelector);
	const isRegularSiteType = useSelector(selectIsRegularSiteType);
	const { gameLauncher } = useGameLauncher();
	const { initializeLogin } = useLoginFlow();
	const { isLoadingTranslations, translations } = useTranslations(textKeys);
	const isFunModeAllowed = useIsFunModeAllowed(game);
	// TODO: remove this logic to a custom hook like useJackpotData etc. since it's duplicating
	const currency = useSelector(selectCurrency);
	const locale = useSelector(selectLocale);
	const jackpots = useSelector(jackpotsSelector);
	const jackpotData = useSelector(state => gameJackpotDataSelector(state, game));
	const { srcSet: inGameSrcSet } = useIngameImage(game);
	const { ROUTE_CASINO_SUBPROVIDER, ROUTE_CASINO_PROVIDER } = useCasinoRoutes();
	const configuration = useConfiguration();
	const { isLiveCasinoLocation } = useCasinoLocation();

	const { amount: realTimeJackpotAmount } = jackpots?.[game?.jackpotName] || {};

	const preferredJackpotAmount = realTimeJackpotAmount || jackpotData || game?.jackpot;
	const formattedJackpotAmount = currencyFormatter.format(preferredJackpotAmount, currency, locale, true);

	const {
		PLAY,
		PRACTICE_MODE,
		JACKPOT,
		PLAY_4_REAL,
		MOBILE_LOGIN_LOGIN_BUTTON,
		MOBILE_TRUSTLY_DEPOSIT_LOGIN,
		LOVEGAMES_LOGOUT
	} = translations;

	const lovedGames = useSelector(lovedGamesSelector);
	const isGameLoved = lovedGames.includes(gameCode);

	const [videoLoaded, setVideoLoaded] = useState(false);
	const [lastGameCode, setLastGameCode] = useState('');

	useEffect(() => {
		if (game && game?.code !== lastGameCode) {
			trackGameInfoOpen({
				game,
				category: isLiveCasinoLocation ? 'LiveCasino' : 'Casino'
			});
			setLastGameCode(game.code);
			setVideoLoaded(false);
		}
	}, [game, lastGameCode, isLiveCasinoLocation]);

	useEffect(() => {
		if (gameCode) {
			disableBodyScroll(gameInfoRef.current);
		} else {
			enableBodyScroll(gameInfoRef.current);
		}
		return () => clearAllBodyScrollLocks();
	}, [gameCode]);

	const playGame = ({ event, mode, depositLogin }) => {
		trackGameInfoPlayClick({
			game,
			mode,
			category: isLiveCasinoLocation ? 'LiveCasino' : 'Casino'
		});
		gameLauncher({
			event,
			mode,
			game,
			depositLogin,
			listName,
			listGameIndex
		});
	};

	const closeGameInfo = () => {
		trackGameInfoClosed({
			game,
			category: isLiveCasinoLocation ? 'LiveCasino' : 'Casino'
		});
		router.back();
	};

	const onToggleLoveGameClick = () => {
		if (!authenticated) {
			initializeLogin({
				from: location.pathname
			});
			dispatch(
				showNotification({
					message: LOVEGAMES_LOGOUT,
					color: 'neutral',
					autoHide: true
				})
			);
			return;
		}
		dispatch(toggleLoveGame(gameCode));
	};

	const getRealButtonText = () => {
		if (!isRegularSiteType && authenticated) {
			return PLAY_4_REAL;
		}
		if (!isRegularSiteType) {
			return MOBILE_TRUSTLY_DEPOSIT_LOGIN;
		}
		return `${PLAY} ${game.name}`;
	};

	const getPlayButtonDataAttribute = () => {
		if (authenticated) {
			return 'game-support-real-mode';
		}

		return isRegularSiteType ? 'game-support-real-mode' : 'game-support-deposit-login';
	};

	const imageComponent = useMemo(
		() => (
			<img
				sizes="(min-width: 64.25em) 16.666vw, (min-width: 48em) 25vw, (min-width: 20em) 50vw"
				src={inGameSrcSet ? getSmallestSrcFromSrcSet(inGameSrcSet) : game?.imageView?.src}
				srcSet={inGameSrcSet || game?.imageView?.srcSet}
				alt={game?.name}
				onError={e => errorHandler(e, configuration?.casino?.tmpGameIcon)}
				loading="lazy"
				data-at="game-support-image"
			/>
		),
		[game?.name, game?.imageView?.src, game?.imageView?.srcSet, inGameSrcSet, configuration?.casino?.tmpGameIcon]
	);

	const videoComponent = useMemo(() => {
		if (game?.otherView?.file && game?.otherView?.path && !inGameSrcSet) {
			return (
				<img
					src={`${game.otherView.path}/${game.otherView.file}`}
					alt={game?.name}
					onLoad={() => setVideoLoaded(game.otherView.file && game.otherView.path)}
					loading="lazy"
				/>
			);
		}
		setVideoLoaded(false);
		return null;
	}, [game?.name, game?.otherView?.path, game?.otherView?.file, inGameSrcSet]);

	const providerPageURL = game?.casinoSubProviderVO
		? transformPath(`${ROUTE_CASINO_SUBPROVIDER}/${game?.casinoSubProviderVO.code}`)
		: transformPath(`${ROUTE_CASINO_PROVIDER}/${game?.provider?.code}`);

	if (isLoadingTranslations) return <div />;

	return (
		<GameInfoContainer
			ref={gameInfoRef}
			regulated={hasRegulationHeader}
			active={gameCode}
			data-at={gameCode ? 'game-support-active' : undefined}
		>
			{game && (
				<>
					<ImageContainer>
						<ExitButton onClick={closeGameInfo} data-at="game-support-close-button">
							<Icon type={ICON_CLOSE} />
						</ExitButton>

						<VisibilityWrapper visible={!videoLoaded}>{imageComponent}</VisibilityWrapper>

						<VisibilityWrapper visible={videoLoaded}>{videoComponent}</VisibilityWrapper>
					</ImageContainer>
					<TitleContainer>
						<GameName data-at="game-info-name">{game.name}</GameName>
						<ProviderName data-at="game-info-provider-name">
							<Link href={providerPageURL}>
								{game?.casinoSubProviderVO ? game?.casinoSubProviderVO?.name : game?.provider?.name}
							</Link>
						</ProviderName>
						<LoveButton onClick={onToggleLoveGameClick} data-at="love-icon">
							<Icon type={isGameLoved ? ICON_HEART_FILLED : ICON_HEART} className="item__love-icon" />
						</LoveButton>
					</TitleContainer>
					{/*
					//Description area will not show for a while
					<DescriptionContainer>
						{game.shortDescription && (
							<p>{game.shortDescription}</p>
						)}
						{game.mediumDescription && (
							<p>{game.mediumDescription}</p>
						)}
						{game.description && <p>{game.description}</p>}
					</DescriptionContainer> */}
					{hasDepositLogin && (
						<InfoButton
							onClick={e => {
								playGame({ event: e, mode: GAME_MODE.REAL });
								if (isCasinoSearchPath(location.pathname)) dispatch(searchConversion(gameCode));
							}}
							data-at="game-support-login-button"
						>
							{MOBILE_LOGIN_LOGIN_BUTTON}
						</InfoButton>
					)}
					{isFunModeAllowed && (
						<InfoButton
							onClick={e => {
								playGame({ event: e, mode: GAME_MODE.FUN });
								if (isCasinoSearchPath(location.pathname)) dispatch(searchConversion(gameCode));
							}}
							data-at="game-support-fun-mode"
						>
							{PRACTICE_MODE}
						</InfoButton>
					)}
					<DetailsContainer>
						{formattedJackpotAmount && (
							<DetailsItem>
								<DetailAmount data-at="jackpot-amount">{formattedJackpotAmount}</DetailAmount>
								<DetailTitle data-at="jackpot-title">{JACKPOT}</DetailTitle>
							</DetailsItem>
						)}
					</DetailsContainer>
					<PlayRealContainer>
						<PlayRealButton
							onClick={e => {
								playGame({
									event: e,
									mode: GAME_MODE.REAL,
									depositLogin: hasDepositLogin
								});
								if (isCasinoSearchPath(location.pathname)) dispatch(searchConversion(gameCode));
							}}
							data-at={getPlayButtonDataAttribute()}
						>
							{getRealButtonText()}
						</PlayRealButton>
					</PlayRealContainer>
				</>
			)}
		</GameInfoContainer>
	);
};

export default GameInfoOld;
